/**
 * Main instructor panel component.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { OverviewMetrics } from '../../components/instructor/OverviewMetrics';
import { UserActivityTable } from '../../components/instructor/UserActivityTable';
import { UserDetailView } from '../../components/instructor/UserDetailView';
import { LabAssignment } from '../../components/instructor/LabAssignment';
import { InstructorService, UserActivityFilters, UserSummary } from '../../services/instructor';
import { useOverviewStats, useUserActivity, useRefreshInstructorData } from '../../hooks/useInstructorQueries';

type ViewState = 'users' | 'overview' | 'lab-assignments';

export function InstructorPanel() {
  const { user, isLoading: authLoading } = useAuth();
  const [view, setView] = useState<ViewState>('users');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [instructors, setInstructors] = useState<UserSummary[]>([]);
  const [instructorFilter, setInstructorFilter] = useState('');
  const [cohortFilter, setCohortFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);

  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (user?.role === 'instructor' && !instructorFilter) {
      setInstructorFilter(String(user.id));
    }
  }, [user, instructorFilter]);

  useEffect(() => {
    let mounted = true;
    InstructorService.getInstructors()
      .then((data) => {
        if (mounted) setInstructors(data);
      })
      .catch((err) => {
        if (mounted) setActionError(err?.message || 'Failed to load instructors.');
      });
    return () => {
      mounted = false;
    };
  }, []);

  const learnerFilters = useMemo<UserActivityFilters>(() => {
    const filters: UserActivityFilters = {
      active_only: activeOnly,
      include_failed: false,
      limit: 200,
    };

    if (searchFilter.trim()) filters.search = searchFilter.trim();
    if (cohortFilter.trim()) filters.cohort = cohortFilter.trim();
    if (instructorFilter === 'unassigned') {
      filters.unassigned = true;
    } else if (instructorFilter) {
      filters.assigned_instructor_id = Number(instructorFilter);
    }

    return filters;
  }, [activeOnly, cohortFilter, instructorFilter, searchFilter]);

  const { data: stats, isLoading: statsLoading, error: statsError } = useOverviewStats(learnerFilters);
  const { data: userActivity = [], isLoading: userActivityLoading, error: userActivityError } = useUserActivity(learnerFilters);
  const refreshData = useRefreshInstructorData();

  const loading = statsLoading || userActivityLoading;
  const error = statsError || userActivityError || actionError;

  const handleChangeView = (nextView: ViewState) => {
    setView(nextView);
    setSelectedUserId(null);
  };

  const handleClearFilters = () => {
    setSearchFilter('');
    setCohortFilter('');
    setActiveOnly(false);
    setInstructorFilter(user?.role === 'instructor' ? String(user.id) : '');
  };

  const handleInstructorAssign = async (learnerId: number, instructorId: number | null) => {
    setActionError(null);
    setActionSuccess(null);
    try {
      await InstructorService.assignLearnerInstructor(learnerId, {
        assigned_instructor_id: instructorId,
      });
      setActionSuccess('Learner instructor assignment updated.');
      refreshData();
    } catch (err: any) {
      setActionError(err?.message || 'Failed to update instructor assignment.');
    }
  };

  if (authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!user || (user.role !== 'admin' && user.role !== 'instructor')) {
    return <Navigate to="/unauthorized" replace />;
  }

  const tabClass = (tab: ViewState) =>
    `px-4 py-2 rounded-lg transition-colors ${
      view === tab ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-700 text-gray-200 hover:bg-gray-600'
    }`;

  const selectedUserSummary = selectedUserId
    ? userActivity.find((summary) => summary.user_id === selectedUserId)
    : null;

  return (
    <div className="p-8 text-white bg-gray-900 min-h-screen" data-testid="instructor-panel">
      <div className="mb-8">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Instructor Dashboard</h1>
            <p className="text-gray-400">
              Find learners, organize cohorts, review grades, and assign labs.
            </p>
          </div>
          <Button onClick={refreshData} disabled={loading}>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
      </div>

      <div className="mb-6 flex flex-wrap gap-3">
        <button onClick={() => handleChangeView('users')} className={tabClass('users')}>
          Learners
        </button>
        <button onClick={() => handleChangeView('overview')} className={tabClass('overview')}>
          Overview
        </button>
        <button
          onClick={() => handleChangeView('lab-assignments')}
          className={tabClass('lab-assignments')}
          data-testid="lab-assignment-tab"
        >
          Lab Assignments
        </button>
      </div>

      {error && (
        <Alert variant="error" className="mb-6" onClose={() => setActionError(null)}>
          {error instanceof Error ? error.message : String(error)}
        </Alert>
      )}
      {actionSuccess && (
        <Alert variant="success" className="mb-6" onClose={() => setActionSuccess(null)}>
          {actionSuccess}
        </Alert>
      )}

      <Card className="p-5 mb-6 bg-gray-800 border-gray-700">
        <div className="grid gap-4 lg:grid-cols-[minmax(220px,1.2fr)_minmax(180px,0.9fr)_minmax(180px,0.9fr)_auto_auto] lg:items-end">
          <Input
            label="Search"
            placeholder="Name, username, email, or cohort"
            value={searchFilter}
            onChange={(event) => setSearchFilter(event.target.value)}
            data-testid="instructor-search"
          />
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1" htmlFor="instructor-filter">
              Instructor
            </label>
            <select
              id="instructor-filter"
              value={instructorFilter}
              onChange={(event) => setInstructorFilter(event.target.value)}
              className="block w-full px-3 py-2 bg-gray-800 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="instructor-filter"
            >
              <option value="">All learners</option>
              <option value="unassigned">Unassigned</option>
              {instructors.map((instructor) => (
                <option key={instructor.id} value={instructor.id}>
                  {instructor.id === user.id ? 'My learners' : (instructor.display_name || instructor.username)}
                </option>
              ))}
            </select>
          </div>
          <Input
            label="Cohort"
            placeholder="Any cohort"
            value={cohortFilter}
            onChange={(event) => setCohortFilter(event.target.value)}
            data-testid="cohort-filter"
          />
          <label className="flex items-center gap-2 text-sm text-gray-300 h-10">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(event) => setActiveOnly(event.target.checked)}
              className="rounded border-gray-600 bg-gray-900"
            />
            Active only
          </label>
          <Button variant="secondary" onClick={handleClearFilters}>
            Clear
          </Button>
        </div>
      </Card>

      {view === 'overview' && (
        <div data-testid="overview-stats">
          <OverviewMetrics stats={stats || null} loading={loading} />
        </div>
      )}

      {view === 'users' && (
        <div className="space-y-6" data-testid="users-tab">
          {selectedUserId ? (
            <div className="space-y-4">
              <Card className="p-4 bg-gray-800 border-gray-700">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <div className="text-sm text-gray-400">Reviewing learner</div>
                    <div className="text-lg font-semibold">
                      {selectedUserSummary?.display_name || selectedUserSummary?.username || 'Selected learner'}
                    </div>
                  </div>
                  <Button variant="secondary" onClick={() => setSelectedUserId(null)} data-testid="back-to-learner-list">
                    Back to Learners
                  </Button>
                </div>
              </Card>
              <UserDetailView
                userId={selectedUserId}
                onBack={() => setSelectedUserId(null)}
                allowFilters={true}
              />
            </div>
          ) : (
            <UserActivityTable
              users={userActivity}
              onUserSelect={setSelectedUserId}
              loading={loading}
              title="Learners"
              instructors={instructors}
              canManageInstructors={isAdmin}
              onInstructorAssign={handleInstructorAssign}
            />
          )}
        </div>
      )}

      {view === 'lab-assignments' && (
        <LabAssignment
          learnerFilters={learnerFilters}
          onAssignmentCreated={refreshData}
        />
      )}
    </div>
  );
}
